import { useState } from "react";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import {
  TableView,
  TableHeader,
  Column,
  TableBody,
  Row,
  Cell,
  Collection,
  Badge,
  ActionButton,
  Switch,
  Text,
} from "@react-spectrum/s2";
import { useDateFormatter } from "react-aria";
import RefreshIcon from "@react-spectrum/s2/icons/Refresh";

import {
  useIntegrationsStatus,
  useRefreshIntegrationsStatus,
} from "@app/hooks/admin";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const columns = [
  { name: "Integration", id: "name", isRowHeader: true },
  { name: "Status", id: "status", width: 160 },
  { name: "Latency  ", id: "latencyMs", align: "end", width: 120 },
  { name: "Detail", id: "message" },
];

// Maps a probe status to a Spectrum Badge variant + human label.
const STATUS_BADGE = {
  operational: { variant: "positive", label: "Operational" },
  degraded: { variant: "notice", label: "Degraded" },
  down: { variant: "negative", label: "Down" },
  not_configured: { variant: "neutral", label: "Not configured" },
};

export const IntegrationsStatus = () => {
  useBreadcrumbs([{ label: "Admin", href: "/admin" }, { label: "API Status" }]);

  const [complexMode, setComplexMode] = useState(false);

  const { data, isLoading, isError, error } =
    useIntegrationsStatus(complexMode);

  const refresh = useRefreshIntegrationsStatus(complexMode);
  const isRefreshing = refresh.isPending;

  const formatter = useDateFormatter({
    dateStyle: "medium",
    timeStyle: "medium",
  });

  const rows = data?.integrations ?? [];
  const loadingState =
    isError || refresh.isError
      ? "error"
      : isLoading || isRefreshing
        ? "loading"
        : "idle";
  const checkedAt = data?.checkedAt ? new Date(data.checkedAt) : null;

  return (
    <div
      className={style({
        display: "flex",
        flexDirection: "column",
        gap: 24,
        size: "full",
        maxHeight: "calc(100dvh - 56px - 48px)",
      })}
    >
      <title>API status | BeStory</title>

      <Switch isSelected={complexMode} onChange={setComplexMode}>
        Complex mode
      </Switch>

      <div
        className={style({
          display: "flex",
          gap: 12,
          alignItems: "center",
          flexWrap: "wrap",
        })}
      >
        <div className={style({ font: "heading" })}>API Status</div>
        <ActionButton
          isQuiet
          size="S"
          staticColor="auto"
          aria-label="Refresh integration status"
          isDisabled={isRefreshing}
          onPress={() => refresh.mutate()}
        >
          <RefreshIcon />
          <Text>{isRefreshing ? "Checking…" : "Refresh"}</Text>
        </ActionButton>
        {checkedAt ? (
          <Text styles={style({ font: "detail", color: "gray-600" })}>
            Last checked {formatter.format(checkedAt)}
          </Text>
        ) : (
          !isLoading && (
            <Text styles={style({ font: "detail", color: "gray-600" })}>
              Never checked — press Refresh to run the test
            </Text>
          )
        )}
      </div>

      <TableView
        aria-label="External API status"
        loadingState={loadingState}
        renderEmptyState={() =>
          isError
            ? (error?.message ?? "Failed to load integration status")
            : "No integrations found"
        }
        overflowMode="wrap"
        density="spacious"
        styles={style({ maxHeight: "full", height: "100%" })}
      >
        <TableHeader columns={columns}>
          {(column) => (
            <Column
              align={column.align}
              isRowHeader={column.isRowHeader}
              width={column.width}
              minWidth={column.minWidth}
            >
              {column.name}
            </Column>
          )}
        </TableHeader>
        <TableBody>
          <Collection items={rows}>
            {(integration) => {
              const badge =
                STATUS_BADGE[integration.status] ?? STATUS_BADGE.down;
              return (
                <Row id={integration.id}>
                  <Cell>{integration.name}</Cell>
                  <Cell>
                    <Badge size="S" variant={badge.variant}>
                      {badge.label}
                    </Badge>
                  </Cell>
                  <Cell align="end">
                    {integration.status === "not_configured"
                      ? "—"
                      : `${integration.latencyMs} ms`}
                  </Cell>
                  <Cell>{integration.message}</Cell>
                </Row>
              );
            }}
          </Collection>
        </TableBody>
      </TableView>
    </div>
  );
};
